import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { HiOutlineAdjustmentsHorizontal, HiChevronDown, HiChevronUp } from "react-icons/hi2";
import { FaPlane, FaMapMarkerAlt, FaSortAmountDownAlt } from "react-icons/fa";
import { baseurl } from "../../baseurl";
import "../../asset/css/flight-results.css";

import AirIndia from "../../asset/img/AirIndia.png";
import Indigo from "../../asset/img/IndiGo.png";
import DefaultAirline from "../../asset/img/logo.png";

const getAirlineLogo = (operatorName) => {
  switch (operatorName) {
    case "IndiGo":
      return Indigo;
      case "Indigo":
      return Indigo; 
    case "Air India":
      return AirIndia; 
      case "Air India Express":
      return AirIndia; 
    default:
      // Fallback to the Skyscanner URL or a local default icon
      return;
  }
};


const FlightResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [flights, setFlights] = useState([]);
  const [filteredFlights, setFilteredFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [resultToken, setResultToken] = useState("");

  // --- FILTER & SORT STATES ---
  const [maxPrice, setMaxPrice] = useState(100000);
  const [stopsFilter, setStopsFilter] = useState("all");
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [sortBy, setSortBy] = useState("cheapest");

  // --- API DATA FETCH ---
  useEffect(() => {
    const performSearch = async () => {
      setLoading(true);
      try {
        const payload = {
          Origin: searchParams.get("fromCode"),
          Destination: searchParams.get("toCode"),
          DepartureDate: searchParams.get("depart"),
          JourneyType: searchParams.get("tripType") === "Roundtrip" ? "RoundTrip" : "OneWay",
          AdultCount: searchParams.get("adults") || 1,
          CabinClass: searchParams.get("cabin") || "Economy"
        };
        const response = await axios.post(`${baseurl}/flightsearch`, payload);
        const token = response.data.externalApiResponse?.Search?.ResultToken;
        setResultToken(token);

        const flightList = response.data.externalApiResponse?.Search?.FlightDataList?.JourneyList?.[0] || [];
        setFlights(flightList);
        setFilteredFlights(flightList);
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setLoading(false);
      }
    };
    if (searchParams.get("fromCode")) performSearch();
  }, [searchParams]);

  // --- DYNAMIC DATA FOR FILTERS ---
  const availableOperators = useMemo(() => {
    const names = flights.map(f => f.FlightDetails.Details[0][0].OperatorName);
    return [...new Set(names)].sort();
  }, [flights]);

  // --- UI HANDLERS ---
  const toggleExpand = (index) => {
    setExpandedId(expandedId === index ? null : index);
  };

  const handleOperatorToggle = (name) => {
    setSelectedOperators(prev => 
      prev.includes(name) ? prev.filter(i => i !== name) : [...prev, name]
    );
  };

  const handleBookNow = (selectedFlight) => {
    const searchData = Object.fromEntries([...searchParams]);
    const flightParam = encodeURIComponent(JSON.stringify(selectedFlight));
    const searchParam = encodeURIComponent(JSON.stringify(searchData));
    const tokenParam = encodeURIComponent(resultToken);
    navigate(`/flight-details?token=${tokenParam}&flight=${flightParam}&search=${searchParam}`);
  };

  // --- FILTER & SORT LOGIC ---
  useEffect(() => {
    let temp = [...flights];

    // 1. Price Filter
    temp = temp.filter(f => f.Price.TotalDisplayFare <= maxPrice);

    // 2. Stops Filter
    if (stopsFilter !== "all") {
      temp = temp.filter(f => (f.FlightDetails.Details[0].length - 1) === parseInt(stopsFilter));
    }

    // 3. Operator Filter
    if (selectedOperators.length > 0) {
      temp = temp.filter(f => selectedOperators.includes(f.FlightDetails.Details[0][0].OperatorName));
    }

    // 4. Sorting
    temp.sort((a, b) => {
      if (sortBy === "cheapest") return a.Price.TotalDisplayFare - b.Price.TotalDisplayFare;
      if (sortBy === "expensive") return b.Price.TotalDisplayFare - a.Price.TotalDisplayFare;
      if (sortBy === "fastest") {
        const durA = a.AccumulatedDuration || a.FlightDetails.Details[0][0].Duration;
        const durB = b.AccumulatedDuration || b.FlightDetails.Details[0][0].Duration;
        return durA - durB;
      }
      return 0;
    });

    setFilteredFlights(temp);
  }, [maxPrice, stopsFilter, selectedOperators, sortBy, flights]);

  if (loading) return <div className="loader-container"><div className="plane-loader"><FaPlane className="moving-plane" /></div><div>Searching Flights for you...</div></div>;

  return (
    <section className="flight-results-bg py-4 min-vh-100 bg-light">
      <div className="container mt-5">
        <div className="row g-4">
          
          {/* --- SIDEBAR FILTERS --- */}
          <div className="col-lg-3">
            <div className="sticky-top" style={{ top: '100px', zIndex: 10 }}>
              <div className="filter-card p-4 shadow-sm bg-white rounded-3 border">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold m-0"><HiOutlineAdjustmentsHorizontal className="me-2"/>Filters</h6>
                  <button className="btn btn-link btn-sm text-decoration-none p-0 text-danger" 
                    onClick={() => { setMaxPrice(100000); setStopsFilter("all"); setSelectedOperators([]); setSortBy("cheapest"); }}>
                    Reset
                  </button>
                </div>

                <hr />

                <div className="mb-2">
                  <label className="small fw-bold text-muted mb-2 d-block">SORT BY</label>
                  <select className="form-select form-select-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="cheapest">Price: Cheapest First</option>
                    <option value="fastest">Duration: Fastest First</option>
                    <option value="expensive">Price: High to Low</option>
                  </select>
                </div>

                {/* <div className="mb-4">
                  <label className="form-label small fw-bold">Max Price: ₹{Math.round(maxPrice).toLocaleString()}</label>
                  <input type="range" className="form-range custom-range" min="2000" max="100000" step="1000" 
                    value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
                </div> */}

                <div className="mb-2">
                  <label className="small fw-bold text-muted mb-2 d-block">STOPS</label>
                  <div className="d-flex flex-column gap-2">
                    {['all', '0', '1', '2'].map(stop => (
                      <div className="form-check" key={stop}>
                        <input className="form-check-input" type="radio" name="stops" id={`stop-${stop}`} 
                          checked={stopsFilter === stop} onChange={() => setStopsFilter(stop)} />
                        <label className="form-check-label small" htmlFor={`stop-${stop}`}>
                          {stop === 'all' ? 'Any Stops' : stop === '0' ? 'Non-Stop' : `${stop} Stop(s)`}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="small fw-bold text-muted mb-2 d-block">AIRLINES</label>
                  <div className="operator-list scroll-bar" style={{maxHeight: '200px', overflowY: 'auto'}}>
                    {availableOperators.map(name => (
                      <div className="form-check mb-2" key={name}>
                        <input className="form-check-input" type="checkbox" id={name}
                          checked={selectedOperators.includes(name)} onChange={() => handleOperatorToggle(name)} />
                        <label className="form-check-label small text-truncate d-block" htmlFor={name}>
                          {name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* --- RESULTS LIST --- */}
          <div className="col-lg-9">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold m-0">{filteredFlights.length} Flights Found</h5>
            </div>

            {filteredFlights.length > 0 ? filteredFlights.map((flight, index) => {
              const details = flight.FlightDetails.Details[0];
              const first = details[0];
              const last = details[details.length - 1];
              const stops = details.length - 1;

              return (
                <div className="flight-bundle mb-3" key={index}>
                  <div className="flight-card-modern shadow-sm border rounded-3 bg-white overflow-hidden">
                    <div className="row align-items-center g-0">
                      <div className="col-md-2 p-3 text-center border-end bg-light-subtle">
  <img 
    src={getAirlineLogo(first.OperatorName)} 
    className="airline-img mb-1" 
    style={{ 
      width: '100px', 
      height: '70px', 
      objectFit: 'contain' // Ensures logos don't stretch
    }} 
    alt={`${first.OperatorName} logo`} 
  />
  <div className="airline-name fw-bold" style={{fontSize: '11px'}}>
    {first.OperatorName}
  </div>
  <div className="text-muted x-small">
    {first.OperatorCode}-{first.FlightNumber}
  </div>
</div>

                      <div className="col-md-7 p-3">
                        <div className="row text-center align-items-center">
                          <div className="col-4">
                            <div className="time-display h5 fw-bold mb-0">{first.Origin.DateTime.split(' ')[1].substring(0,5)}</div>
                            <div className="city-code fw-bold text-muted">{first.Origin.AirportCode}</div>
                            <div className="x-small text-muted">{first.Origin.CityName}</div>
                          </div>
                          <div className="col-4">
                            <div className="duration-label text-muted x-small">{(flight.AccumulatedDuration || first.Duration)} Mins</div>
                            <div className="flight-route-line my-1"><div className="route-line"></div><FaPlane className="route-plane text-teal" /></div>
                            <div className="stop-info x-small fw-bold text-teal">{stops === 0 ? 'Non-Stop' : `${stops} Stop(s)`}</div>
                          </div>
                          <div className="col-4">
                            <div className="time-display h5 fw-bold mb-0">{last.Destination.DateTime.split(' ')[1].substring(0,5)}</div>
                            <div className="city-code fw-bold text-muted">{last.Destination.AirportCode}</div>
                            <div className="x-small text-muted">{last.Destination.CityName}</div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-3 p-3 text-center border-start">
                        <div className="main-price mb-2 h4 fw-bold text-dark">₹{Math.round(flight.Price.TotalDisplayFare).toLocaleString()}</div>
                        <button className="btn btn-teal-action w-100 btn-sm mb-2 fw-bold py-2" onClick={() => handleBookNow(flight)}>
                           Book Now
                        </button>
                        <div className="x-small text-primary cursor-pointer fw-bold" onClick={() => toggleExpand(index)}>
                          {expandedId === index ? <><HiChevronUp /> Hide Details</> : <><HiChevronDown /> Flight Details</>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {expandedId === index && (
                    <div className="details-expand-box p-4 bg-white border border-top-0 rounded-bottom-3 shadow-sm">
                       <h6 className="fw-bold mb-3"><FaMapMarkerAlt className="text-teal me-2"/>Itinerary Breakdown</h6>
                       {details.map((segment, sIdx) => (
                         <div key={sIdx} className="mb-3 border-start border-2 border-teal ps-3">
                           <div className="fw-bold small">{segment.Origin.CityName} ({segment.Origin.AirportCode}) → {segment.Destination.CityName} ({segment.Destination.AirportCode})</div>
                           <div className="text-muted x-small">{segment.OperatorName} | {segment.Duration} Mins | {segment.Attr.CabinClass}</div>
                           {sIdx < details.length - 1 && <div className="p-1 px-2 my-2 bg-light small rounded border">Layover: {segment.Destination.CityName}</div>}
                         </div>
                       ))}
                    </div>
                  )}
                </div>
              );
            }) : (
              <div className="text-center py-5 bg-white border rounded shadow-sm">
                <FaSortAmountDownAlt size={40} className="text-muted mb-3"/>
                <h5>No flights match your filters</h5>
                <p className="text-muted">Try adjusting the price or stop settings.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FlightResult;